import * as React from 'react';
import { createPortal } from 'react-dom';
import { Outlet } from 'react-router-dom';

import { Header } from '@/app/shell/header';
import { usePhoneSystemHealth } from '@/app/providers/phone-system-provider';
import { PhoneSystemNotice } from '@/app/shell/phone-system-notice';
import { Sidebar, SidebarContentReveal, SidebarPanel } from './sidebar';
import { SidebarProvider } from '@/app/shell/sidebar/sidebar-context';

const TITLE_SLOT_ID = 'wer-page-title-slot';
const ACTIONS_SLOT_ID = 'wer-page-actions-slot';
const CENTER_SLOT_ID = 'wer-page-center-slot';

/**
 * Routes contribute their title and header buttons by portalling into slots the
 * header renders. Portals rather than context state on purpose: an inline JSX
 * action node would be a new object every render, and storing that in state
 * re-renders forever.
 */
function SlotPortal({ slotId, children }: { slotId: string; children: React.ReactNode }) {
  const [node, setNode] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    setNode(document.getElementById(slotId));
  }, [slotId]);

  if (!node) return null;
  return createPortal(children, node);
}

export function PageTitle({ children }: { children: React.ReactNode }) {
  return (
    <SlotPortal slotId={TITLE_SLOT_ID}>
      <h1 className="truncate text-sm font-semibold text-ink">{children}</h1>
    </SlotPortal>
  );
}

export function PageHeaderActions({ children }: { children: React.ReactNode }) {
  return <SlotPortal slotId={ACTIONS_SLOT_ID}>{children}</SlotPortal>;
}

/** Header content centred between the title and the actions (e.g. a search box). */
export function PageHeaderCenter({ children }: { children: React.ReactNode }) {
  return <SlotPortal slotId={CENTER_SLOT_ID}>{children}</SlotPortal>;
}

/** Sets document.title, which the portal-based PageTitle does not cover. */
export function useDocumentTitle(title: string): void {
  React.useEffect(() => {
    const previous = document.title;
    document.title = title ? `${title} · Admin Console` : 'Admin Console';
    return () => {
      document.title = previous;
    };
  }, [title]);
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const phoneSystem = usePhoneSystemHealth();

  return (
    <SidebarProvider>
      <div className="flex h-full">
        <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        {/* When the sidebar is collapsed on desktop, the content column owns
            the left edge: cursor there peels the page back (or slides the
            sidebar in) to reveal navigation. */}
        <SidebarContentReveal panel={<SidebarPanel footer="pin" />}>
          <Header
            onOpenSidebar={() => setMobileOpen(true)}
            titleSlotId={TITLE_SLOT_ID}
            centerSlotId={CENTER_SLOT_ID}
            actionsSlotId={ACTIONS_SLOT_ID}
          />
          {phoneSystem?.backend_healthy === false ? <PhoneSystemNotice health={phoneSystem} /> : null}
          <main className="flex-1 overflow-y-auto scrollbar-thin">
            <Outlet />
          </main>
        </SidebarContentReveal>
      </div>
    </SidebarProvider>
  );
}

/** Standard page padding + max width, so every route lines up. */
export function PageContainer({
  children,
  className = '',
  wide = false,
}: {
  children: React.ReactNode;
  className?: string;
  wide?: boolean;
}) {
  return (
    <div className={`mx-auto w-full ${wide ? 'max-w-none' : 'max-w-[100rem]'} p-3 sm:p-4 ${className}`}>
      {children}
    </div>
  );
}
